import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import classNames from 'classnames';
import { createSelector } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import { ButtonV2 } from '@praktio/common-ui';

import { RootState } from '../../../../ducks/store';
import { getTeams } from '../../../../ducks/teams';
import { NavSection, setDrillDown } from '../../../../ducks/features/navPanelSlice';
import { buildTeamHierarchy } from '../../../../utils/functions';

import { highlightMatch } from '../../../../utils/text';
import NavPanelSkeleton from '../NavPanelSkeleton';
import PanelSearch from '../PanelSearch';
import useSharedTooltip from '../hooks/useSharedTooltip';
import useSharedDropdown from '../hooks/useSharedDropdown';

import './TeamsPanel.scss';

const ITEM_HEIGHT = 36;

interface IProps {
  visible: boolean;
}

interface IOrderedNode {
  _id: string;
  name: string;
  parent?: string;
  children?: ITeam[];
  depth: number;
}

interface TeamRowData {
  nodes: IOrderedNode[];
  teamId: string;
  searchQuery: string;
  query: string;
  expandedNodes: Record<string, boolean>;
  onDrillDown: (node: IOrderedNode) => void;
  onToggleExpand: (nodeId: string, expanded: boolean) => void;
  showTooltip: (e: React.MouseEvent, text: string) => void;
  hideTooltip: () => void;
  toggleMenu: (e: React.MouseEvent, itemId: string) => void;
}

const TeamRow: FC<ListChildComponentProps<TeamRowData>> = ({ index, style, data }) => {
  const {
    nodes,
    teamId,
    searchQuery,
    query,
    expandedNodes,
    onDrillDown,
    onToggleExpand,
    showTooltip,
    hideTooltip,
    toggleMenu,
  } = data;
  const n = nodes[index];
  const expanded = expandedNodes[n._id];
  const hasChildren = !!n.children?.length;
  const isActive = teamId === n._id;

  return (
    <div style={style}>
      <div
        className={classNames('TeamsPanel-node', {
          'TeamsPanel-node--active': isActive,
        })}
        role="treeitem"
        aria-expanded={hasChildren ? expanded : undefined}
        onClick={() => onDrillDown(n)}
        onMouseEnter={(e) => showTooltip(e, n.name)}
        onMouseLeave={hideTooltip}
      >
        <div className="TeamsPanel-node-spacing" style={{ marginLeft: `${n.depth * 1.5}rem` }}>
          {hasChildren ? (
            <span
              onClick={(e) => {
                e.stopPropagation();
                if (!query) {
                  onToggleExpand(n._id, !expanded);
                }
              }}
              className="material-icons TeamsPanel-node-chevron"
            >
              {expanded ? 'expand_more' : 'chevron_right'}
            </span>
          ) : null}
        </div>
        <div className="TeamsPanel-node-label">
          <span className="TeamsPanel-node-name">{highlightMatch(n.name, searchQuery)}</span>
          <div
            className="TeamsPanel-node-menu"
            onClick={(e: React.MouseEvent) => toggleMenu(e, n._id)}
          >
            <span className="TeamsPanel-node-menu-toggle">
              <span className="material-icons">more_horiz</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const buildTeamMap = (nodes: ITeam[]): Record<string, ITeam> => {
  const map: Record<string, ITeam> = {};
  const walk = (list: ITeam[]) => {
    for (const n of list) {
      map[n._id] = n;
      if (n.children) walk(n.children);
    }
  };
  walk(nodes);
  return map;
};

const selectTeamsData = createSelector(
  (state: RootState) => state.teams.list,
  (state: RootState) => state.navPanel.drillDown[NavSection.Teams],
  (teams, drillDown) => {
    const teamsHierarchy = buildTeamHierarchy({ teams: teams || [], sorted: true });
    const teamById = buildTeamMap(teamsHierarchy || []);
    return { teamsHierarchy, teamById, drillDown };
  },
);

const TeamsPanel: FC<IProps> = ({ visible }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const teamId = params.teamId as string;

  const { teamsHierarchy, teamById, drillDown } = useSelector(selectTeamsData);
  const rawTeams = useSelector((state: RootState) => state.teams.list);

  const [fetchDone, setFetchDone] = useState(!!rawTeams?.length);
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (visible && !fetchDone) {
      (async () => {
        await (dispatch as any)(getTeams());
        setFetchDone(true);
      })();
    }
  }, [visible, fetchDone, dispatch]);

  const isLoading = !fetchDone && !teamsHierarchy?.length;
  const showEmpty = fetchDone && !teamsHierarchy?.length;

  // Drill down to the topmost ancestor of the given node and expand the subtree.
  // Returns true if a drill-down was actually dispatched.
  const drillDownToTopAncestor = useCallback(
    (nodeId: string): boolean => {
      let ancestor = teamById[nodeId];
      if (!ancestor) return false;

      while (ancestor.parent && teamById[ancestor.parent]) {
        ancestor = teamById[ancestor.parent];
      }

      // No subtree to drill into (top-level team has no children).
      if (!ancestor.children?.length) return false;

      dispatch(
        setDrillDown({
          section: NavSection.Teams,
          drillDown: { rootId: ancestor._id, breadcrumb: ancestor.name },
        }),
      );

      const expandAll: Record<string, boolean> = {};
      const collectIds = (teams: ITeam[]) => {
        for (const t of teams) {
          if (t.children?.length) {
            expandAll[t._id] = true;
            collectIds(t.children);
          }
        }
      };
      collectIds([ancestor]);
      setExpandedNodes((prev) => ({ ...prev, ...expandAll }));

      return true;
    },
    [teamById, dispatch],
  );

  // Restore drill-down state after page refresh when viewing a team with subteams
  useEffect(() => {
    if (!fetchDone || !teamId || drillDown) return;
    drillDownToTopAncestor(teamId);
  }, [fetchDone, teamId, drillDown, drillDownToTopAncestor]);

  // Build the ordered node list from hierarchy (same algorithm as TeamsTree)
  const orderedNodes = useMemo(() => {
    const sourceTeams = (() => {
      if (!drillDown) return teamsHierarchy || [];
      const root = teamById[drillDown.rootId];
      return root ? [{ ...root, parent: undefined }] : [];
    })();

    const queue = sourceTeams.map((t: ITeam) => cloneDeep(t));
    const depthById: Record<string, number> = {};
    const collapsedParents: Record<string, boolean> = {};
    const nodes: IOrderedNode[] = [];

    let item = queue.shift();
    while (item) {
      const { _id, children, parent } = item;
      const parentDepth = parent ? depthById[parent] ?? -1 : -1;
      const depth = drillDown ? parentDepth + 1 : parent ? parentDepth + 1 : 0;
      depthById[_id] = depth;

      const isParentVisible =
        !parent || !teamById[parent] || (expandedNodes[parent] && !collapsedParents[parent]);
      const isDrillDownRoot = drillDown && !parent;
      const shouldShow = isDrillDownRoot || isParentVisible;

      if (shouldShow) {
        nodes.push({ ...item, depth: Math.max(depth, 0) });
      } else {
        collapsedParents[_id] = true;
      }

      if (children) {
        queue.unshift(...children);
      }

      item = queue.shift();
    }

    return nodes;
  }, [teamsHierarchy, drillDown, expandedNodes, teamById]);

  const query = searchQuery.trim().toLowerCase();

  // Hierarchy-preserving search: shows matches + ancestors + descendants
  const searchResults = useMemo(() => {
    if (!query) return null;

    const sourceTeams = (() => {
      if (!drillDown) return teamsHierarchy || [];
      const root = teamById[drillDown.rootId];
      return root ? [{ ...root, parent: undefined }] : [];
    })();

    // Build a scoped node map from the source trees
    const scopedMap: Record<string, ITeam> = {};
    const buildScopedMap = (nodes: ITeam[]) => {
      for (const n of nodes) {
        scopedMap[n._id] = n;
        if (n.children) buildScopedMap(n.children);
      }
    };
    buildScopedMap(sourceTeams);

    // Step 1: Find matching IDs
    const matchingIds = new Set<string>();
    for (const id of Object.keys(scopedMap)) {
      if (scopedMap[id].name.toLowerCase().includes(query)) {
        matchingIds.add(id);
      }
    }

    // Step 2: Collect descendant IDs of matches
    const descendantIds = new Set<string>();
    const collectDescendants = (node: ITeam) => {
      if (node.children) {
        for (const child of node.children) {
          descendantIds.add(child._id);
          collectDescendants(child);
        }
      }
    };
    matchingIds.forEach((id) => {
      const node = scopedMap[id];
      if (node) collectDescendants(node);
    });

    // Step 3: Collect ancestor IDs (clamped to scope)
    const ancestorIds = new Set<string>();
    const ancestorCache = new Map<string, string[]>();
    const getAncestors = (nodeId: string): string[] => {
      if (ancestorCache.has(nodeId)) return ancestorCache.get(nodeId)!;
      const node = scopedMap[nodeId];
      if (!node?.parent || !scopedMap[node.parent]) {
        ancestorCache.set(nodeId, []);
        return [];
      }
      const ancestors = [node.parent, ...getAncestors(node.parent)];
      ancestorCache.set(nodeId, ancestors);
      return ancestors;
    };

    matchingIds.forEach((id) => {
      getAncestors(id).forEach((a) => ancestorIds.add(a));
    });
    descendantIds.forEach((id) => {
      getAncestors(id).forEach((a) => ancestorIds.add(a));
    });

    const includedIds = new Set([...matchingIds, ...ancestorIds, ...descendantIds]);

    // Step 4: Compute depth via memoization
    const depthCache = new Map<string, number>();
    const getDepth = (nodeId: string): number => {
      if (depthCache.has(nodeId)) return depthCache.get(nodeId)!;
      const node = scopedMap[nodeId];
      if (!node?.parent || !scopedMap[node.parent]) {
        depthCache.set(nodeId, 0);
        return 0;
      }
      const depth = getDepth(node.parent) + 1;
      depthCache.set(nodeId, depth);
      return depth;
    };

    // Step 5: Traverse source trees in order, emit included nodes
    const results: IOrderedNode[] = [];
    const traverse = (nodes: ITeam[]) => {
      for (const node of nodes) {
        if (includedIds.has(node._id)) {
          results.push({ ...node, depth: getDepth(node._id) });
        }
        if (node.children) {
          traverse(node.children);
        }
      }
    };
    traverse(sourceTeams);

    return results;
  }, [query, teamsHierarchy, drillDown, teamById]);

  const displayNodes = searchResults ?? orderedNodes;
  const showNoResults = query && displayNodes.length === 0;

  const handleDrillDown = useCallback(
    (node: IOrderedNode) => {
      if (!drillDown) {
        const drilled = drillDownToTopAncestor(node._id);
        if (drilled) setSearchQuery('');
      }
      navigate(`/admin/teams/${node._id}`);
    },
    [drillDown, drillDownToTopAncestor, navigate],
  );

  const onToggleExpand = useCallback((nodeId: string, expanded: boolean) => {
    setExpandedNodes((prev) => ({ ...prev, [nodeId]: expanded }));
  }, []);

  const { showTooltip, hideTooltip, tooltipOverlay } = useSharedTooltip();
  const { toggleMenu, renderMenu } = useSharedDropdown();

  const menuItems = useMemo(
    () => [
      {
        label: 'New Subteam',
        onClick: (itemId: string) => {
          const team = teamById[itemId];
          const name = team?.name ?? '';
          navigate(`/admin/teams/new?parentId=${itemId}&parentName=${name}`);
        },
      },
    ],
    [navigate, teamById],
  );

  const hasItems = displayNodes.length > 0;

  const itemData: TeamRowData | null = useMemo(
    () =>
      hasItems
        ? {
            nodes: displayNodes,
            teamId,
            searchQuery,
            query,
            expandedNodes,
            onDrillDown: handleDrillDown,
            onToggleExpand,
            showTooltip,
            hideTooltip,
            toggleMenu,
          }
        : null,
    [
      hasItems,
      displayNodes,
      teamId,
      searchQuery,
      query,
      expandedNodes,
      handleDrillDown,
      onToggleExpand,
      showTooltip,
      hideTooltip,
      toggleMenu,
    ],
  );

  return (
    <div className="NavPanel-body TeamsPanel" role="tree">
      <PanelSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search..." />
      {isLoading ? <NavPanelSkeleton /> : null}
      {showEmpty ? <div className="NavPanel-empty">No teams found</div> : null}
      {showNoResults ? <div className="PanelSearch-empty">No matching teams</div> : null}
      {hasItems && itemData ? (
        <div className="NavPanel-list-container">
          <AutoSizer>
            {({ height, width }: Size) => (
              <FixedSizeList
                className="TeamsPanel-list NavPanel-list dark-scroll"
                height={height}
                width={width}
                itemCount={displayNodes.length}
                itemSize={ITEM_HEIGHT}
                itemData={itemData}
              >
                {TeamRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        </div>
      ) : null}
      <div className="NavPanel-footer">
        <ButtonV2
          className="NavPanel-footer-btn"
          variant="outline"
          onClick={() => navigate('/admin/teams/new')}
        >
          + NEW TEAM
        </ButtonV2>
      </div>
      {tooltipOverlay}
      {renderMenu(menuItems)}
    </div>
  );
};

export default React.memo(TeamsPanel);
